/**
 * MessageBubble Component
 *
 * Individual message bubble in a conversation thread.
 */

import React from 'react';
import type { Message } from '../hooks/useMessages';

interface MessageBubbleProps {
  message: Message;
  isOwn: boolean;
  senderName?: string;
  senderPhoto?: string;
}

const formatTime = (date: Date): string => {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  } else if (days === 1) {
    return 'Yesterday';
  } else if (days < 7) {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  } else {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  }
};

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isOwn,
  senderName,
  senderPhoto,
}) => {
  return (
    <div className={`flex gap-3 ${isOwn ? 'flex-row-reverse' : ''}`}>
      {/* Avatar (only for other's messages) */}
      {!isOwn && (
        <div className="flex-shrink-0">
          {senderPhoto ? (
            <img
              src={senderPhoto}
              alt={senderName || 'User'}
              className="w-8 h-8 rounded-full object-cover"
            />
          ) : (
            <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-gray-500 text-sm">
              {senderName?.charAt(0) || 'U'}
            </div>
          )}
        </div>
      )}

      {/* Message Content */}
      <div className={`max-w-[75%] ${isOwn ? 'items-end' : 'items-start'}`}>
        <div
          className={`px-4 py-2 rounded-2xl ${
            isOwn
              ? 'bg-primary-500 text-white rounded-tr-sm'
              : 'bg-gray-100 text-gray-900 rounded-tl-sm'
          }`}
        >
          <p className="whitespace-pre-wrap break-words">{message.content}</p>
        </div>
        <p
          className={`text-xs text-gray-400 mt-1 ${isOwn ? 'text-right' : 'text-left'}`}
        >
          {formatTime(message.createdAt)}
          {isOwn && message.read && (
            <span className="ml-2 text-gray-400">Read</span>
          )}
        </p>
      </div>
    </div>
  );
};

export default MessageBubble;
